export enum SystemDiagnosticMetricStatusEnum {
    NORMAL = 'normal',
    ERRORED = 'errored'
}

export enum SystemDiagnosticMetricEnum {
    TIME_ON_GRID = 'time_on_grid',
    INTERNET_CONNECTIVITY = 'internet_connectivity',
    DAILY_AVERAGE_BATTERY_DISCHARGE = 'daily_average_battery_discharge',
    DAILY_AVERAGE_SOLAR_PRODUCTION = 'daily_average_solar_production',
    LOAD_DROPS = 'load_drops',
    NEGATIVE_SOLAR_LOAD = 'negative_solar_load',
    NEGATIVE_HOME_LOAD = 'negative_home_load',
    FLOATING_GROUND = 'floating_ground',
    GRID_COMPLIANCE = 'grid_compliance',
    POWERWALL_DISCONNECTED = 'powerwall_disconnected'
}

export const SYSTEM_DIAGNOSTIC_METRICS = [
    {
        key: SystemDiagnosticMetricEnum.TIME_ON_GRID,
        title: 'Time On-Grid',
        description: '% of time over the past 7 days the system has been connected to the grid. Low values caused by' +
            ' frequent power outages, or bad wiring between grid and Island Controller. If low, check alerts and grid' +
            ' connection to island controller.',
        format(value?: number) {
            return typeof value === 'number' ? `${value}%` : undefined
        },
        status(value?: number) {
            return !value || value < 10
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    },
    {
        key: SystemDiagnosticMetricEnum.INTERNET_CONNECTIVITY,
        title: 'Internet Connectivity',
        description: '% of time over the last 7 days the system is connected to the internet. Low values usually' +
            ' caused by poor cellular or Wi-Fi signals. If low, check the connection type and extend Wi-Fi or' +
            ' Ethernet to the Gateway.',
        format(value?: number) {
            return typeof value === 'number' ? `${value}%` : undefined
        },
        status(value?: number) {
            return !value || value < 10
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    },
    {
        key: SystemDiagnosticMetricEnum.DAILY_AVERAGE_BATTERY_DISCHARGE,
        title: 'Daily Average Battery Discharge',
        description: 'Average battery discharge per day for the past 7 days. Low values caused by faulted or disabled' +
            ' Powerwall unit. If low check alerts, enable lines and inverter states of all Batteries.',
        format(value?: number) {
            return typeof value === 'number' ? `${value} kWh/day` : undefined
        },
        status(value?: number) {
            return !value || value < 10
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    },
    {
        key: SystemDiagnosticMetricEnum.DAILY_AVERAGE_SOLAR_PRODUCTION,
        title: 'Daily Average Solar Production',
        description: 'Average solar production per day for the past 7 days. Low values caused by faulted 3rd party' +
            ' inverter (or solar meter MIA) or faulted/disabled Powerwall+ Solar Inverter. If low check alerts,' +
            ' enable lines and inverter states of all Inverters',
        format(value?: number) {
            return typeof value === 'number' ? `${value} kWh/day` : undefined
        },
        status(value?: number) {
            return !value || value < 10
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    },
    {
        key: SystemDiagnosticMetricEnum.LOAD_DROPS,
        title: 'Load Drops',
        description: 'Number of times over the past 7 days the system has dropped loads. This is commonly caused by' +
            ' low energy, overload or hardware faults. If present, check off-grid states and alerts.',
        status(value?: number) {
            return value && value > 0
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    },
    {
        key: SystemDiagnosticMetricEnum.NEGATIVE_SOLAR_LOAD,
        title: 'Negative Solar',
        description: 'Fails if solar was below -250W > 1% of the past 7 days. Caused by incorrect solar meter' +
            ' install. If present check all Solar meters for correct install and configuration.',
        format(value?: number) {
            return typeof value === 'number' ? `${value}%` : undefined
        },
        status(value?: number) {
            return value && value > 0
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    },
    {
        key: SystemDiagnosticMetricEnum.NEGATIVE_HOME_LOAD,
        title: 'Negative Home Load',
        description: 'Fails if home load below -100W > 1% of the past 7 days. Caused by unmetered solar generation' +
            ' or incomplete site metering. If present check all Solar and Site meters for correct install and' +
            ' configuration.',
        format(value?: number) {
            return typeof value === 'number' ? `${value}%` : undefined
        },
        status(value?: number) {
            return value && value > 0
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    },
    {
        key: SystemDiagnosticMetricEnum.FLOATING_GROUND,
        title: 'Floating Ground',
        description: 'Maximum Neutral Chassis Voltage over the last 7 days; fails if >20V. Caused by incorrect' +
            ' Neutral wiring and will prevent backup operation. If present, escalate to Tier 2 for review.',
        format(value?: number) {
            return typeof value === 'number' ? `${value}mV` : undefined
        },
        status(value?: number) {
            return value && value > 0
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    },
    {
        key: SystemDiagnosticMetricEnum.GRID_COMPLIANCE,
        title: 'Grid Compliance',
        description: '% of time over the last 7 days the system has any ECU reporting an un-compliant grid. This is' +
            ' commonly caused by a bad quality grid connection or incomplete installation.',
        format(value?: number) {
            return typeof value === 'number' ? `${value}%` : undefined
        },
        status(value?: number) {
            return value && value > 0
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    },
    {
        key: SystemDiagnosticMetricEnum.POWERWALL_DISCONNECTED,
        title: 'Powerwall Disconnected',
        description: 'Fails if Powerwall has a disconnected alert for >2% of time over the last 7 days. This is' +
            ' caused by an tripped or open circuit breaker, or incorrect wiring to the Powerwall. If present, check' +
            ' Powerwall voltages and reset all circuit breakers.',
        format(value?: number) {
            return typeof value === 'number' ? `${value}%` : undefined
        },
        status(value?: number) {
            return value && value > 0
                ? SystemDiagnosticMetricStatusEnum.ERRORED : SystemDiagnosticMetricStatusEnum.NORMAL
        }
    }
]
